import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const statusColor = (status) => {
  if (status === 'resolved') return 'success';
  if (status === 'contacted') return 'info';
  return 'warning';
};

export default function VyaparFailedOtp() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/franchisee/admin/vyapar-failed-otp`, {
        headers: authHeaders(),
      });
      setRows(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load failed OTP attempts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = async (id, followUpStatus) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/franchisee/admin/vyapar-failed-otp/${id}`,
        { follow_up_status: followUpStatus },
        { headers: authHeaders() },
      );
      setRows((current) =>
        current.map((row) => (row.id === id ? { ...row, follow_up_status: followUpStatus } : row)),
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not update follow-up');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">vyapar_failedotp</Typography>
          <Typography variant="body2" color="text.secondary">
            Mobile OTP failures from Vyapar. Follow up, then the shop can publish and save bills after verification.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Shop</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Why</TableCell>
              <TableCell>Verified now</TableCell>
              <TableCell>Follow-up</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7}>No failed OTP attempts.</TableCell>
              </TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</TableCell>
                <TableCell>{row.live_business_name || row.business_name || '—'}</TableCell>
                <TableCell>{row.live_phone || row.phone || '—'}</TableCell>
                <TableCell>{row.live_email || row.email || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2">{row.failure_reason || 'OTP failed'}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.source || ''}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.phone_otp_verified ? 'Yes' : 'No'}
                    color={row.phone_otp_verified ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={row.follow_up_status || 'open'}
                    onChange={(event) => updateRow(row.id, event.target.value)}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="contacted">Contacted</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
